import { RecommendedBadge } from '../../common/RecommendedBadge';
import { Tooltip } from '../../common/Tooltip';
import type { GenerationModel } from '../../../features/generation-models/api';
import styles from './ImageModelFamilySelector.module.css';

interface VideoModelFamilySelectorProps {
  models: GenerationModel[];
  activeModelCode: string;
  onSelect: (modelCode: string) => void;
  disabled?: boolean;
  /** 全ボタンを選べなくする理由 (全編モーションなど)。指定すると薄く表示し、ツールチップにこの文言を出す。 */
  disabledReason?: string;
  // 参考画像 / ストーリーボードモードのとき true。r2v / mmref を持たないモデル
  // (Veo Lite 等) をボタン単位で無効化する。
  requireReferenceSupport?: boolean;
  // 無効化時のツールチップに使うモード名 ("参考画像" / "ストーリーボード")。
  referenceModeLabel?: string;
}

/** プロバイダ表示順とアイコン・フォールバック表記。未掲載のプロバイダは末尾に並ぶ。 */
const PROVIDER_ORDER = ['seedance', 'omni', 'veo', 'minimax', 'kling'] as const;
export const PROVIDER_META: Record<string, { label: string; icon: string; iconMask?: string }> = {
  omni: { label: 'Google Gemini Omni', icon: '', iconMask: '/gemini_icon_white.svg' },
  veo: { label: 'Google Veo 3.1', icon: '', iconMask: '/gemini_icon_white.svg' },
  seedance: {
    label: 'ByteDance Seedance 2.0',
    icon: '',
    iconMask: '/logos/bytedance_icon_white.svg',
  },
  minimax: { label: 'MiniMax', icon: '', iconMask: '/logos/minimax_icon_white.svg' },
  kling: { label: 'Kuaishou Kling', icon: '🎞️' },
};

export function videoProviderRank(provider: string | null | undefined): number {
  const i = PROVIDER_ORDER.indexOf((provider ?? '') as (typeof PROVIDER_ORDER)[number]);
  return i === -1 ? PROVIDER_ORDER.length : i;
}

/**
 * グループ見出し。generation_models.metadata.ui_group_label (マスタデータ) が正本
 * (例 'ByteDance Seedance' / 'Google Gemini' / 'MiniMax')。未設定行は
 * PROVIDER_META → provider 名の順でフォールバックする。
 */
export function videoModelGroupLabel(m: GenerationModel): string {
  const meta = m.metadata as { ui_group_label?: string } | null | undefined;
  if (meta?.ui_group_label) return meta.ui_group_label;
  const provider = m.provider ?? 'unknown';
  return PROVIDER_META[provider]?.label ?? provider;
}

/**
 * ボタン / ドロップダウン項目の表記。metadata.ui_series_label ('2.5' / 'Omni' 等) が
 * あれば display_label に前置する (例 '2.5 標準' / 'Omni スピード')。
 */
export function videoModelButtonLabel(m: GenerationModel): string {
  const meta = m.metadata as { ui_series_label?: string } | null | undefined;
  return meta?.ui_series_label ? `${meta.ui_series_label} ${m.display_label}` : m.display_label;
}

/**
 * ツールチップ見出し用の「プロバイダ側の正式なモデル名」。
 * display_label ('標準' / 'スピード' / 'ミニ') や model_family_name は当サービス独自の
 * 呼称なので、ここでは使わない。
 *   1. metadata.provider_model_name (整形済みの正式名を持たせたい場合の任意項目)
 *   2. provider_endpoint (プロバイダ API のモデル ID。例 'dreamina-seedance-2-0-mini-260615')
 *   3. どちらも無ければ独自表記でフォールバック
 */
export function videoModelProviderName(m: GenerationModel): string {
  const meta = m.metadata as { provider_model_name?: string } | null | undefined;
  const official = meta?.provider_model_name?.trim();
  if (official) return official;
  const endpoint = m.provider_endpoint?.trim();
  if (endpoint) return endpoint;
  const family = m.model_family_name?.trim();
  if (!family) return `${videoModelGroupLabel(m)} ${videoModelButtonLabel(m)}`.trim();
  const tier = m.display_label?.trim() ?? '';
  if (!tier || family.includes(tier)) return family;
  return `${family} ${tier}`;
}

/**
 * 動画生成モデル選択 UI。metadata.ui_group_label 単位でカード化し、同一グループ内の
 * 世代・系列は ui_series_label 接頭辞付きボタンで並べる (Seedance 2.5 / 2.0 等)。
 */
export function VideoModelFamilySelector({
  models,
  activeModelCode,
  onSelect,
  disabled = false,
  disabledReason,
  requireReferenceSupport = false,
  referenceModeLabel = '参考画像',
}: VideoModelFamilySelectorProps) {
  const grouped = new Map<string, GenerationModel[]>();
  for (const m of models) {
    const key = videoModelGroupLabel(m);
    const arr = grouped.get(key) ?? [];
    arr.push(m);
    grouped.set(key, arr);
  }

  // グループ順はメンバーの provider 順 (PROVIDER_ORDER) → sort_order で決める
  const groupRank = (key: string) => {
    const list = grouped.get(key) ?? [];
    return Math.min(...list.map((m) => videoProviderRank(m.provider)));
  };
  const groupMinSort = (key: string) => {
    const list = grouped.get(key) ?? [];
    return Math.min(...list.map((m) => m.sort_order));
  };
  const orderedGroupKeys: string[] = Array.from(grouped.keys()).sort((a, b) => {
    const rankDiff = groupRank(a) - groupRank(b);
    if (rankDiff !== 0) return rankDiff;
    return groupMinSort(a) - groupMinSort(b);
  });

  const renderModelButton = (m: GenerationModel) => {
    const isActive = activeModelCode === m.model_code;
    const supportsRef = (m.supported_modes ?? []).includes('r2v')
      || (m.supported_modes ?? []).includes('mmref');
    const incompatibleWithRefMode = requireReferenceSupport && !supportsRef;
    const isDisabled = disabled || incompatibleWithRefMode || !!disabledReason;
    const tooltipBody = disabledReason
      ? disabledReason
      : incompatibleWithRefMode
        ? `${referenceModeLabel}モードでは利用できません`
        : (m.description ?? '');
    // 薄く見せるのは「選べない理由がある」とき (参考画像モード非対応 / 全編モーション)
    const dimmed = incompatibleWithRefMode || !!disabledReason;
    const button = (
      <button
        type="button"
        className={`${styles.optionButton} ${isActive ? styles.optionButtonActive : ''}`}
        onClick={() => onSelect(m.model_code)}
        disabled={isDisabled}
        style={{
          // disabled 要素はマウスイベントを発火しないため、ラッパー (Tooltip) 側で
          // hover を拾えるようポインタイベントを透過させる
          ...(isDisabled ? { pointerEvents: 'none' as const } : null),
          ...(dimmed ? { opacity: 0.4, cursor: 'not-allowed' } : null),
        }}
      >
        {m.is_recommended && <RecommendedBadge active={isActive} />}
        <span>{videoModelButtonLabel(m)}</span>
      </button>
    );
    // モデル説明はボタン下の常時表示ではなく hover ツールチップで見せる
    // (説明文の追加でレイアウトが伸び縮みしないようにするため)。
    // 冒頭にプロバイダ側の正式なモデル名を見出しとして出す。
    const tooltipContent = (
      <>
        <span className={styles.modelTooltipTitle}>{videoModelProviderName(m)}</span>
        {tooltipBody && <span className={styles.modelTooltipBody}>{tooltipBody}</span>}
      </>
    );
    return (
      <Tooltip
        key={m.id}
        content={tooltipContent}
        className={`${styles.optionButtonTooltip}${isDisabled ? ` ${styles.optionButtonTooltipDisabled}` : ''}`}
      >
        {button}
      </Tooltip>
    );
  };

  return (
    <>
      {orderedGroupKeys.map((groupKey) => {
        const list = grouped.get(groupKey) ?? [];
        const sorted = [...list].sort((a, b) => a.sort_order - b.sort_order);
        // アイコンはグループ先頭モデルの provider のものを使う
        // (Google Gemini グループは Omni / Veo とも同じ Gemini アイコン)
        const providerMeta = PROVIDER_META[sorted[0]?.provider ?? ''] ?? { label: groupKey, icon: '🎞️' };

        return (
          <div key={groupKey} className={styles.modelFamilyGroup}>
            <div className={styles.modelFamilyHeader}>
              {providerMeta.iconMask ? (
                <span
                  className={styles.modelFamilyIconMask}
                  role="img"
                  aria-label={groupKey}
                  style={{
                    WebkitMaskImage: `url("${providerMeta.iconMask}")`,
                    maskImage: `url("${providerMeta.iconMask}")`,
                  }}
                />
              ) : (
                <span className={styles.modelFamilyIcon} role="img" aria-label={groupKey}>
                  {providerMeta.icon}
                </span>
              )}
              <span>{groupKey}</span>
            </div>
            <div className={styles.modelQualityRow}>{sorted.map(renderModelButton)}</div>
          </div>
        );
      })}
    </>
  );
}
